import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Dimensions, FlatList, Linking, Modal, Platform, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { ReportConfigRow } from '../../../../store/slices/reportingConfigurationSlice';
import { useTheme } from '../../../../theme/useTheme';
import { AdminColors } from '../../constants/adminColors';
import { FilterKey, REPORTING_TABLE_COLUMNS, SortKey } from '../../constants/reportingConfiguration';
import styles from './ReportingConfiguration.styles';

interface ReportingConfigurationTableProps {
  rows: ReportConfigRow[];
  sortedRows: ReportConfigRow[];
  activeCount: number;
  disabledCount: number;
  searchValue: string;
  setSearchValue: (value: string) => void;
  hasAppliedColumnFilters: boolean;
  clearAllFilters: () => void;
  toggleSort: (key: SortKey) => void;
  renderSortIcon: (key: SortKey) => React.ReactNode;
  openEditModal: (row: ReportConfigRow) => void;
  renderBadge: (label: string, variant: 'green' | 'gray' | 'blue', inTableDarkMode?: boolean) => React.ReactNode;
  activeFilterKey: FilterKey | null;
  setActiveFilterKey: (key: FilterKey | null) => void;
  openFilterPopover: (key: FilterKey) => void;
  isFilterActive: (key: FilterKey) => boolean;
  filterButtonRefs: React.MutableRefObject<Partial<Record<FilterKey, any>>>;
  onToggleStatus: (row: ReportConfigRow) => void;
  statusToggleInProgressId?: string | null;
  loading?: boolean;
}

// Helper to estimate text width for responsive column sizing
const estimateTextWidth = (text: string, isBold: boolean = false): number => {
  const baseWidth = Platform.OS === 'web' ? 8 : 6;
  const boldMultiplier = isBold ? 1.2 : 1;
  let charCount = 0;
  const str = String(text);
  for (let i = 0; i < str.length; i++) {
    if (/[WM]/.test(str[i])) {
      charCount += 1.3;
    } else if (/[ilt1|]/.test(str[i])) {
      charCount += 0.6;
    } else {
      charCount += 1;
    }
  }
  return charCount * baseWidth * boldMultiplier;
};

const ReportingConfigurationTable: React.FC<ReportingConfigurationTableProps> = ({
  rows,
  sortedRows,
  activeCount,
  disabledCount,
  searchValue,
  setSearchValue,
  hasAppliedColumnFilters,
  clearAllFilters,
  toggleSort,
  renderSortIcon,
  openEditModal,
  renderBadge,
  activeFilterKey,
  setActiveFilterKey,
  openFilterPopover,
  isFilterActive,
  filterButtonRefs,
  onToggleStatus,
  statusToggleInProgressId = null,
  loading = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ReportConfigRow | null>(null);

  const windowWidth = Dimensions.get('window').width;
  const isMobileIos = Platform.OS === 'ios' && windowWidth < 768;

  // Use the predefined column widths from REPORTING_TABLE_COLUMNS
  const totalWidth = useMemo(() => {
    return REPORTING_TABLE_COLUMNS.reduce((sum, col) => sum + col.width, 0);
  }, []);

  // On web desktop: fit all columns within viewport using percentage widths (no horizontal scroll)
  // On smaller screens or native: use fixed widths with horizontal scroll
  const isDesktopWeb = Platform.OS === 'web' && windowWidth >= 1024;

  // Get column style based on platform/viewport
  const getColumnStyle = (key: string) => {
    const col = REPORTING_TABLE_COLUMNS.find((c) => c.key === key);
    if (!col) return {};
    if (isDesktopWeb) {
      // Use percentage of total to fit within container - no horizontal scroll
      const pct = (col.width / totalWidth) * 100;
      return { width: `${pct}%` as any };
    }
    return { width: col.width };
  };

  const openDetailsModal = (row: ReportConfigRow) => {
    setSelectedRow(row);
    setDetailsVisible(true);
  };

  const closeDetailsModal = () => {
    setDetailsVisible(false);
    setSelectedRow(null);
  };

  const renderMobileCard = ({ item }: { item: ReportConfigRow }) => (
    <View style={[localStyles.mobileCardContainer, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
      <View style={localStyles.mobileCardHeader}>
        <View style={[localStyles.mobileCardIconWrap, isDark && { backgroundColor: 'rgba(61,139,255,0.15)' }]}>
          <MaterialIcons name={item.icon as any} size={18} color={isDark ? colors.brand.primary : AdminColors.blue700} />
        </View>
        <View style={localStyles.mobileCardTitleWrap}>
          <TouchableOpacity onPress={() => openEditModal(item)}>
            <Text style={[localStyles.mobileCardTitle, isDark && { color: colors.brand.primary }]} numberOfLines={1}>{item.name}</Text>
          </TouchableOpacity>
          <View style={localStyles.mobileCardStatusRow}>
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={() => onToggleStatus(item)}
                disabled={statusToggleInProgressId === item.id}
              >
                {renderBadge(item.status, item.status === 'Active' ? 'green' : 'gray', isDark)}
              </TouchableOpacity>
              {renderBadge(item.primaryTag, 'blue', isDark)}
          </View>
        </View>
      </View>

      {item.description ? (
        <Text style={[localStyles.mobileCardDescription, isDark && { color: colors.text.secondary }]} numberOfLines={2}>{item.description}</Text>
      ) : null}

      <View style={localStyles.mobileCardFooter}>
        <TouchableOpacity style={localStyles.viewDetailsButton} onPress={() => openDetailsModal(item)}>
          <Text style={[localStyles.viewDetailsText, isDark && { color: colors.brand.primary }]}>View Details</Text>
          <MaterialIcons name="chevron-right" size={16} color={isDark ? colors.brand.primary : AdminColors.blue700} />
        </TouchableOpacity>
      </View>
    </View>
  );

  if (isMobileIos) {
    return (
      <View style={[styles.tableContainer, isDark && { backgroundColor: colors.background.surface }]}>
        {/* Toolbar */}
        <View style={[styles.tableToolbar, localStyles.mobileToolbar, isDark && { borderBottomColor: colors.border.divider }]}>
          <View style={[styles.searchBox, isSearchFocused && styles.searchBoxFocused, localStyles.mobileSearchBox, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}>
            <MaterialIcons name="search" size={16} color={isDark ? colors.text.disabled : AdminColors.gray350} />
            <TextInput
              style={[styles.searchInput, { outlineStyle: 'none' } as any, isDark && { color: colors.text.primary }]}
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search reports..."
              placeholderTextColor={isDark ? colors.text.disabled : AdminColors.gray350}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
            />
          </View>
          <View style={localStyles.mobileToolbarMeta}>
            <Text style={[styles.tableCount, isDark && { color: colors.text.secondary }]}>
              {rows.length} reports ({activeCount} Active, {disabledCount} Disabled)
            </Text>
            {hasAppliedColumnFilters ? (
              <TouchableOpacity onPress={clearAllFilters} style={[localStyles.mobileClearFilterButton, isDark && { borderColor: colors.brand.primary }]}>
                <Text style={[styles.clearFilterText, isDark && { color: colors.brand.primary }]}>Clear Filters</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>

        {/* Content */}
        <View style={[styles.tableContentWrapper, { position: 'relative' }]}>
          {loading && (
            <View style={[localStyles.loadingOverlay, isDark && { backgroundColor: 'rgba(20,20,20,0.8)' }]}>
              <View style={localStyles.loadingSpinnerBox}>
                <ActivityIndicator size="large" color={colors.brand.primary} />
                <Text style={[localStyles.loadingSpinnerText, isDark && { color: colors.text.primary }]}>Loading...</Text>
              </View>
            </View>
          )}

          {!loading && sortedRows.length === 0 ? (
            <View style={localStyles.mobileEmptyState}>
              <MaterialIcons name="info-outline" size={28} color={isDark ? colors.text.disabled : '#ADB5BD'} />
              <Text style={[localStyles.mobileEmptyText, isDark && { color: colors.text.secondary }]}>No reports match your search or filters.</Text>
            </View>
          ) : (
            <FlatList
              data={sortedRows}
              keyExtractor={(item) => String(item.id)}
              renderItem={renderMobileCard}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={localStyles.mobileListContent}
              ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
              scrollEnabled={false}
              removeClippedSubviews={false}
            />
          )}
        </View>

        {/* Footer */}
        <View style={styles.tableFooter}>
          <Text style={styles.tableFooterText}>
            Showing {sortedRows.length} of {rows.length} reports
          </Text>
        </View>

        {/* Details Modal */}
        <Modal visible={detailsVisible} animationType="slide" transparent onRequestClose={closeDetailsModal}>
          <View style={localStyles.modalBackdrop}>
            <View style={[localStyles.modalCard, isDark && { backgroundColor: colors.background.elevated }]}>
              <View style={localStyles.modalHeader}>
                <Text style={[localStyles.modalTitle, isDark && { color: colors.text.primary }]}>{selectedRow?.name || 'Report Details'}</Text>
                <TouchableOpacity onPress={closeDetailsModal}>
                  <MaterialIcons name="close" size={22} color={isDark ? colors.text.primary : '#3D4147'} />
                </TouchableOpacity>
              </View>

              <ScrollView showsVerticalScrollIndicator={false}>
                {selectedRow ? (
                  <>
                    <View style={[localStyles.modalDetailRow, isDark && { borderBottomColor: colors.border.divider }]}>
                      <Text style={[localStyles.modalDetailLabel, isDark && { color: colors.text.disabled }]}>Status</Text>
                      <View style={localStyles.modalBadgeWrap}>
                        {renderBadge(selectedRow.status, selectedRow.status === 'Active' ? 'green' : 'gray')}
                      </View>
                    </View>

                    <View style={[localStyles.modalDetailRow, isDark && { borderBottomColor: colors.border.divider }]}>
                      <Text style={[localStyles.modalDetailLabel, isDark && { color: colors.text.disabled }]}>Primary Tag</Text>
                      <View style={localStyles.modalBadgeWrap}>
                        {renderBadge(selectedRow.primaryTag, 'blue', isDark)}
                      </View>
                    </View>

                    <View style={[localStyles.modalDetailRow, isDark && { borderBottomColor: colors.border.divider }]}>
                      <Text style={[localStyles.modalDetailLabel, isDark && { color: colors.text.disabled }]}>Secondary Tags</Text>
                      <View style={localStyles.modalBadgeWrap}>
                        {selectedRow.secondaryTags.length > 0
                          ? selectedRow.secondaryTags.map((tag) => (
                              <View key={`modal-sec-${tag}`}>{renderBadge(tag, 'gray', isDark)}</View>
                            ))
                          : <Text style={[localStyles.modalMutedText, isDark && { color: colors.text.disabled }]}>—</Text>}
                      </View>
                    </View>

                    <View style={[localStyles.modalDetailRow, isDark && { borderBottomColor: colors.border.divider }]}>
                      <Text style={[localStyles.modalDetailLabel, isDark && { color: colors.text.disabled }]}>KPI Tags</Text>
                      <View style={localStyles.modalBadgeWrap}>
                        {selectedRow.kpiTags.length > 0
                          ? selectedRow.kpiTags.map((tag) => (
                              <View key={`modal-kpi-${tag}`}>{renderBadge(tag, 'gray', isDark)}</View>
                            ))
                          : <Text style={[localStyles.modalMutedText, isDark && { color: colors.text.disabled }]}>—</Text>}
                      </View>
                    </View>

                    <View style={[localStyles.modalDetailRow, isDark && { borderBottomColor: colors.border.divider }]}>
                      <Text style={[localStyles.modalDetailLabel, isDark && { color: colors.text.disabled }]}>Description</Text>
                      <Text style={[localStyles.modalDetailValue, isDark && { color: colors.text.primary }]}>
                        {selectedRow.description || '—'}
                      </Text>
                    </View>

                    <View style={[localStyles.modalDetailRow, isDark && { borderBottomColor: colors.border.divider }]}>
                      <Text style={[localStyles.modalDetailLabel, isDark && { color: colors.text.disabled }]}>Link</Text>
                      <Text style={[localStyles.modalLinkValue, isDark && { color: colors.brand.primary }]} numberOfLines={2}>
                        {selectedRow.link || '—'}
                      </Text>
                    </View>

                    <TouchableOpacity
                      style={localStyles.modalEditButton}
                      onPress={() => {
                        closeDetailsModal();
                        openEditModal(selectedRow);
                      }}
                    >
                      <MaterialIcons name="edit" size={16} color="#FFFFFF" />
                      <Text style={localStyles.modalEditButtonText}>Edit Report</Text>
                    </TouchableOpacity>
                  </>
                ) : null}
              </ScrollView>
            </View>
          </View>
        </Modal>
      </View>
    );
  }

  // Desktop / iPad / Web layout
  return (
    <View style={[styles.tableContainer, isDark && { backgroundColor: colors.background.surface }]}>
      <View style={[styles.tableToolbar, isDark && { borderBottomColor: colors.border.divider }]}>
        <View style={[styles.searchBox, isSearchFocused && styles.searchBoxFocused, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}>
          <MaterialIcons name="search" size={16} color={isDark ? colors.text.disabled : AdminColors.gray350} />
          <TextInput
            style={[styles.searchInput, { outlineStyle: 'none' } as any, isDark && { color: colors.text.primary }]}
            value={searchValue}
            onChangeText={setSearchValue}
            placeholder="Search reports..."
            placeholderTextColor={isDark ? colors.text.disabled : AdminColors.gray350}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />
        </View>
        <View style={styles.tableToolbarRight}>
          <Text style={[styles.tableCount, isDark && { color: colors.text.secondary }]}>
            {rows.length} reports ({activeCount} Active, {disabledCount} Disabled)
          </Text>
          {hasAppliedColumnFilters ? (
            <TouchableOpacity onPress={clearAllFilters} style={[styles.clearFilterButton, isDark && { backgroundColor: 'transparent', borderColor: colors.brand.primary }]}>
              <Text style={[styles.clearFilterText, isDark && { color: colors.brand.primary }]}>Clear Filters</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      <View style={[styles.tableContentWrapper, { position: 'relative' }]}>
        {loading && (
          <View style={[localStyles.loadingOverlay, isDark && { backgroundColor: 'rgba(20,20,20,0.8)' }]}>
            <View style={localStyles.loadingSpinnerBox}>
              <ActivityIndicator size="large" color={colors.brand.primary} />
              <Text style={[localStyles.loadingSpinnerText, isDark && { color: colors.text.primary }]}>Loading...</Text>
            </View>
          </View>
        )}
        <ScrollView
          horizontal={!isDesktopWeb}
          nestedScrollEnabled
          directionalLockEnabled
          keyboardShouldPersistTaps="handled"
          showsHorizontalScrollIndicator={!isDesktopWeb}
          contentContainerStyle={[
            localStyles.tableScrollContent,
            isDesktopWeb && { width: '100%' as any },
            isDark && { backgroundColor: colors.background.elevated },
          ]}
          style={[localStyles.tableScroll, isDark && { backgroundColor: colors.background.elevated }]}
        >
        <View style={[
          localStyles.tableContent,
          isDesktopWeb
            ? { width: '100%' as any }
            : { minWidth: totalWidth },
          isDark && { backgroundColor: colors.background.elevated },
        ]}>
          <View style={[styles.headerRowTable, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.default }]}>
            {REPORTING_TABLE_COLUMNS.map((column) => {
              return (
                <View
                  key={column.key}
                  style={[
                    styles.headerCell,
                    getColumnStyle(column.key),
                    isDark && { borderBottomColor: colors.border.default, backgroundColor: colors.background.surface },
                  ]}
                >
                  <TouchableOpacity
                    style={styles.headerSortButton}
                    activeOpacity={0.8}
                    onPress={() => toggleSort(column.key)}
                  >
                    <Text style={[styles.headerCellText, isDark && { color: colors.text.secondary }]}>{column.label}</Text>
                    {renderSortIcon(column.key)}
                  </TouchableOpacity>
                  {column.filterable ? (
                    <TouchableOpacity
                      style={[styles.headerFilterButton, isDark && { backgroundColor: 'transparent' }]}
                      ref={(node) => {
                        if (column.filterable) {
                          filterButtonRefs.current[column.key as FilterKey] = node;
                        }
                      }}
                      onPress={() => {
                        const key = column.key as FilterKey;
                        if (activeFilterKey === key) {
                          setActiveFilterKey(null);
                          return;
                        }
                        openFilterPopover(key);
                      }}
                    >
                      <MaterialIcons
                        name="filter-list"
                        size={14}
                        color={isFilterActive(column.key as FilterKey) ? colors.brand.primary : (isDark ? colors.text.disabled : AdminColors.gray300)}
                      />
                    </TouchableOpacity>
                  ) : null}
                </View>
              );
            })}
          </View>

          <View style={{ position: 'relative', minHeight: loading && sortedRows.length === 0 ? 180 : undefined }}>
          {sortedRows.length === 0 && !loading ? (
            <View style={localStyles.emptyRow}>
              <Text style={[localStyles.emptyText, isDark && { color: colors.text.secondary }]}>No reports match your search or filters.</Text>
            </View>
          ) : (
            sortedRows.map((row) => (
              <View key={row.id} style={[styles.tableRow, isDark && { backgroundColor: colors.background.elevated, borderBottomColor: colors.border.divider }]}>
                <View style={[styles.tableCell, getColumnStyle('icon'), { alignItems: 'center' }]}>
                  <View style={[styles.iconWrap, isDark && { backgroundColor: 'rgba(61,139,255,0.15)' }]}>
                    <MaterialIcons name={row.icon as any} size={18} color={isDark ? colors.brand.primary : AdminColors.blue700} />
                  </View>
                </View>
                <View style={[styles.tableCell, getColumnStyle('name')]}>
                  <TouchableOpacity onPress={() => openEditModal(row)}>
                    <Text style={[styles.reportLink, isDark && { color: colors.brand.primary }]}>{row.name}</Text>
                  </TouchableOpacity>
                </View>
                <View style={[styles.tableCell, getColumnStyle('primaryTag')]}>
                  {renderBadge(row.primaryTag, 'blue', isDark)}
                </View>
                <View style={[styles.tableCell, getColumnStyle('secondaryTags')]}>
                  <View style={styles.badgeWrap}>
                    {row.secondaryTags.length > 0
                      ? row.secondaryTags.map((tag) => <View key={`${row.id}-${tag}`}>{renderBadge(tag, 'gray', isDark)}</View>)
                      : <Text style={[styles.mutedText, isDark && { color: colors.text.disabled }]}>-</Text>}
                  </View>
                </View>
                <View style={[styles.tableCell, getColumnStyle('kpiTags')]}>
                  <View style={styles.badgeWrap}>
                    {row.kpiTags.length > 0
                      ? row.kpiTags.map((tag) => <View key={`${row.id}-kpi-${tag}`}>{renderBadge(tag, 'gray', isDark)}</View>)
                      : <Text style={[styles.mutedText, isDark && { color: colors.text.disabled }]}>-</Text>}
                  </View>
                </View>
                <View style={[styles.tableCell, getColumnStyle('status')]}>
                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={() => onToggleStatus(row)}
                    disabled={statusToggleInProgressId === row.id}
                  >
                    {renderBadge(row.status, row.status === 'Active' ? 'green' : 'gray', isDark)}
                  </TouchableOpacity>
                </View>
                <View style={[styles.tableCell, getColumnStyle('description')]}>
                  <Text style={[styles.bodyText, isDark && { color: colors.text.primary }]} numberOfLines={2}>
                    {row.description}
                  </Text>
                </View>
                <View style={[styles.tableCell, getColumnStyle('link')]}>
                  {row.link ? (
                    <TouchableOpacity
                      onPress={() => {
                        if (Platform.OS === 'web') {
                          window.open(row.link, '_blank');
                        } else {
                          Linking.openURL(row.link);
                        }
                      }}
                    >
                      <Text style={[styles.linkText, { textDecorationLine: 'underline' }, isDark && { color: colors.brand.primary }]} numberOfLines={1}>
                        {row.link}
                      </Text>
                    </TouchableOpacity>
                  ) : (
                    <Text style={[styles.mutedText, isDark && { color: colors.text.disabled }]}>-</Text>
                  )}
                </View>
              </View>
            ))
          )}
          </View>
        </View>
      </ScrollView>
      </View>

      <View style={[styles.tableFooter, isDark && { borderTopColor: colors.border.divider }]}>
        <Text style={[styles.tableFooterText, isDark && { color: colors.text.secondary }]}>
          Showing {sortedRows.length} of {rows.length} reports
        </Text>
      </View>
    </View>
  );
};

export default ReportingConfigurationTable;

const localStyles = StyleSheet.create({
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    borderRadius: 4,
  },
  loadingSpinnerBox: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  loadingSpinnerText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#3D4147',
  },
  tableScroll: {
    width: '100%',
    flex: 1,
  },
  tableScrollContent: {
    flexGrow: 1,
  },
  tableContent: {
    flex: 1,
    width: '100%',
  },
  emptyRow: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#6B6E72',
    fontWeight: '400',
  },
  // ─── Mobile styles ───────────────────────────────────────────────────────
  mobileToolbar: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
  },
  mobileSearchBox: {
    minWidth: 0,
    width: '100%',
  },
  mobileToolbarMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mobileClearFilterButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: AdminColors.blue700,
    borderRadius: 999,
    backgroundColor: '#FFFFFF',
  },
  mobileListContent: {
    padding: 12,
  },
  mobileCardContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E1E5EA',
    padding: 14,
    width: '100%',
  },
  mobileCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  mobileCardIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: AdminColors.blue50 ?? '#EBF3FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileCardTitleWrap: {
    flex: 1,
    gap: 4,
  },
  mobileCardTitle: {
    fontSize: 15,
    color: AdminColors.textLink ?? '#0B57B7',
    fontWeight: '700',
  },
  mobileCardStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  mobileCardDescription: {
    fontSize: 13,
    lineHeight: 18,
    color: '#6B6E72',
    marginBottom: 8,
  },
  mobileCardFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 4,
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  viewDetailsText: {
    fontSize: 14,
    color: AdminColors.blue700,
    fontWeight: '700',
  },
  mobileEmptyState: {
    minHeight: 140,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  mobileEmptyText: {
    fontSize: 14,
    color: '#6B6E72',
    textAlign: 'center',
  },
  // ─── Modal styles ────────────────────────────────────────────────────────
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(20, 28, 38, 0.45)',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  modalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    maxHeight: '85%',
    padding: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    color: '#2D2F33',
    fontWeight: '700',
    flex: 1,
  },
  modalDetailRow: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2F6',
  },
  modalDetailLabel: {
    fontSize: 12,
    color: '#9CA3AF',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 6,
  },
  modalDetailValue: {
    fontSize: 14,
    color: '#3D4147',
    lineHeight: 20,
  },
  modalLinkValue: {
    fontSize: 13,
    color: AdminColors.textLink ?? '#0B57B7',
    lineHeight: 18,
  },
  modalBadgeWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  modalMutedText: {
    fontSize: 13,
    color: '#9CA3AF',
  },
  modalEditButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 20,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: AdminColors.blue700,
  },
  modalEditButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});
